// The overworld chart under the World screen (mockup "Overworld — click a node to walk there"):
// a deterministic valley seeded from the World, so every player in it sees the same map, and a
// different World draws a different one. The base is a char grid for `terrainFromRows` in the
// season's kit (grass, tree belts, water with shores); on top of it the screen paints `fills`
// (mud, flagstones) and `props` (peaks, trees, planks, keeps, cottages) straight from the world atlas.
//
// Each place looks like what its name says (`placeLook`): "Horle's River Crossing" stands on a
// bridge over the river, "Graystone Castle" is a walled keep, "the Ancient Forest" sits inside a
// wood, "Blackfir Mere" on a lake shore, "Elkford" on stepping stones. The head word of the name
// decides the kind, other words add touches (a "Drowned Coppice" is a wood in the mud), and the
// place's slot fills in when the name says nothing (a crossing always has water).
//
// The seed moves everything else: where the places stand, which way the river runs and how it
// bends, the lakes, the ranges, how wooded the valley is, and which roads join which places.
// Roads are A* paths over the grid (round mountains and keeps, over water only by a bridge or a
// ford, merging where they meet), along a random spanning tree plus sometimes one loop.
import { FENCE_WOOD, FILLS, GROUND, LIQUID, LIQUID_FILL, LIQUID_STONE, PLANKS, PROPS, WALL16 } from '../../../game/tiling/oryx';
import { E as EAST, N as NORTH, S as SOUTH, W as WEST } from '../../../game/tiling/grid';
import type { Biome } from './overworldBiomes';
import { mulberry, noise, planRegions, type OwRegion, type RegionSpec } from './overworldRegions';

export type { OwRegion, RegionSpec } from './overworldRegions';

/** The still mud fill (the mud liquid's first ripple frame). */
const MUD = LIQUID.mud!.base + LIQUID_FILL[0];

export const OW_W = 44, OW_H = 26;
/** The cell at the middle of the free map area (the screen centres it between its panels). */
export const OW_CX = 22, OW_CY = 13;

/** What a place looks like on the chart. */
export type PlaceKind = 'bridge' | 'ford' | 'castle' | 'tower' | 'town' | 'forest' | 'bog' | 'lake' | 'peak' | 'cave' | 'ruin' | 'shrine' | 'road' | 'field';
/** The forge slot a location fills (fk-schema `PlaceSlot`), or the World's own seat. */
export type PlaceSlot = 'home' | 'wild' | 'road' | 'crossing';
export interface PlaceLook {
  kind: PlaceKind;
  /** Other kinds the name or text touches on (trees round a mere, mud under a causeway). */
  tags: PlaceKind[];
  /** Water runs by (or, for a bridge or ford, under) the place. */
  river: boolean;
  /** Dressed stone rather than timber (a stone bridge, the Nine Arches). */
  stone: boolean;
}
/**
 * `kind` overrides what the name reads as (docs/51: a village is a town whatever it is called). `biome` is the region
 * the place stands in on a map of regions (docs/185 PB13); without one it stands in the seat's region.
 */
export interface MapSite { key: string; name: string; text?: string; slot?: PlaceSlot; kind?: PlaceKind; biome?: Biome }
export interface MapProp { frame: number; x: number; y: number }
export interface MapNode { x: number; y: number }
/** A road between two places (`b` is `edge:*` for a road that runs off the chart). */
export interface MapRoad { a: string; b: string; path: Array<[number, number]> }
export interface Overworld {
  w: number; h: number; seed: number; rows: string[]; fills: MapProp[]; props: MapProp[]; nodes: Record<string, MapNode>; roads: MapRoad[]; looks: Record<string, PlaceLook>;
  /**
   * What each cell is, whatever the kit draws there (docs/70, read by the custom overworld art):
   * `~` water, `M` mountain, `b` bog, `a` the autumn ring round a bog, `p` pines, `T` woods,
   * `x` built (keeps, cottages, decks), `.` open ground.
   */
  land: string[];
  /**
   * The custom tile each place stands as on the World chart (docs/143 OW2): what its name reads as, finer
   * than its kind (an abbey, a barrow, a port, a mine); undefined where the art at the node is the
   * crossing itself (a bridge or a ford).
   */
  arts: Record<string, string | undefined>;
  /** The axis each crossing's bridge or ford runs along (docs/143 OW5): roads meet it only end on. */
  crossings: Record<string, 'ns' | 'ew'>;
  /** docs/185 PB13: a map of biome regions has them here (absent on a one-country map). */
  regions?: OwRegion[];
  /** Each cell's region, as a row of letters (`a` is `regions[0]`); with `regions`. */
  regionOf?: string[];
}

// ───────────────────────── reading a name ─────────────────────────

/** Word rules, in priority order within a word. Compounds match by suffix ("Kingsford", "Rimewater"). */
const WORDS: ReadonlyArray<readonly [PlaceKind, RegExp]> = [
  ['bridge', /bridge|arch(es)?$|^crossing$|^weir$|^span$|tollhouse/],
  ['ford', /ford$|^fords$|^shallows$|stepping/],
  ['castle', /castle|^keep$|^fort|fortress|citadel|stronghold|palace|^hold$|bastion|rampart|garrison|manor|^hall$|^court$/],
  ['tower', /tower|^spire$|^watch|beacon|lighthouse|^steeple$/],
  ['shrine', /shrine|temple|abbey|chapel|church|priory|kirk$|altar|sanctum|sanctuary|^well$|^spring$/],
  ['ruin', /ruin|barrow|gallows|cairn|henge|^stones$|graves?$|tomb|crypt|^wreck$|remnant|^rest$/],
  ['town', /town|village|hamlet|market|^inn$|tavern|haven|harbou?r|^port$|^mill$|farm|stead$|wick$|.{3}ton$|.{3}by$|burg|borough|bury$|.{3}ham$|thorpe$|.{3}mark$|^quay$/],
  ['cave', /cave|cavern|^mines?$|delve|grotto|^pit$|^deeps?$|undercroft|warren|^hollow$/],
  ['lake', /^meres?$|.mere$|^lakes?$|loch|^pool|tarn$|lagoon|^pond|water$|^waters$/],
  ['bog', /^bog|marsh|^fens?$|.fen$|mire$|swamp|sedge|^moss$|slough|morass|drowned|sallow|reeds?$|^carr$/],
  ['forest', /wood|forest|grove|thicket|copse|coppice|^holt$|weald|glade|orchard|firs?$|pines?$|^oaks?$|^elms?$|^ash$|alder|^hollin$|holly|^yews?$|birch|^carr$|tangle|^wilds?$/],
  ['peak', /mount|^peaks?$|crag|^fells?$|^tor$|^pass$|cliff|ridge|summit|heights?$|^scar$|^hills?$|^pike$|knoll|^rocks?$|^ben$|stone$/],
  ['road', /road$|^way$|^lane$|^drove$|causeway|^track$|^path$|^street$|highway|^trail$/],
];
/** Words that bring water without naming a kind (the river under "Horle's River Crossing"). */
const RIVER_WORDS = /river|stream|brook|beck$|^burn$|rill|^falls?$|weir|ford|^banks?$|^wash$|harbou?r|^port$|^quay|wharf|^docks?$/;
/** Words that imply a second kind. */
const IMPLIES: ReadonlyArray<readonly [RegExp, PlaceKind]> = [[/causeway|drowned|^carr$/, 'bog'], [/^carr$|coppice/, 'forest']];
const STONE_WORDS = /stone|arch|tollhouse|^nine$|king|old|grey|gray/;
const SLOT_KIND: Record<PlaceSlot, PlaceKind> = { home: 'town', wild: 'forest', road: 'road', crossing: 'bridge' };

const wordsOf = (s: string) => s.toLowerCase().replace(/['’]s\b/g, '').split(/[^a-z]+/).filter((w) => w.length > 1 && w !== 'the' && w !== 'of');
const kindOfWord = (w: string): PlaceKind | undefined => WORDS.find(([, re]) => re.test(w))?.[0];

/**
 * What a place looks like: the kind its name's head word gives (the last word that names one),
 * touches from the rest of the name (and the first two from its text), then the slot when the
 * name says nothing. A crossing always runs over water; so does any name with a river word in it.
 */
export function placeLook(name: string, text = '', slot?: PlaceSlot): PlaceLook {
  const words = wordsOf(name);
  const named = words.map(kindOfWord);
  const head = [...named].reverse().find((k) => k !== undefined);
  const tags = new Set<PlaceKind>(named.filter((k): k is PlaceKind => k !== undefined));
  for (const w of words) for (const [re, k] of IMPLIES) if (re.test(w)) tags.add(k);
  const told = wordsOf(text).map(kindOfWord).filter((k): k is PlaceKind => k !== undefined && k !== 'road' && k !== 'town');
  for (const k of told.slice(0, 2)) tags.add(k);
  const kind = head ?? told[0] ?? (slot ? SLOT_KIND[slot] : 'field');
  tags.delete(kind);
  const river = kind === 'bridge' || kind === 'ford' || tags.has('bridge') || tags.has('ford') || words.some((w) => RIVER_WORDS.test(w)) || slot === 'crossing';
  return { kind, tags: [...tags], river, stone: words.some((w) => STONE_WORDS.test(w)) || kind === 'castle' };
}

/**
 * The custom tile a place's name reads as (docs/143 OW2), finer than `placeLook`'s kind: words first,
 * the last that names one winning as the head word does, then the kind. The World's seat is a town (or
 * its castle, or a port); a village (docs/51, `kind: 'town'`) is a village unless its name says port,
 * farm or mill. A crossing has no tile of its own: its bridge or ford is the art at the node.
 */
const ART_WORDS: ReadonlyArray<readonly [string, RegExp]> = [
  ['abbey', /abbey|temple|priory|monaster|church|kirk$|minster|cathedral|cloister|friary|convent/],
  ['shrine', /shrine|chapel|altar|sanctum|sanctuary|^well$|^spring$|oratory|^cross$/],
  ['graveyard', /graves?$|graveyard|tomb|crypt|gallows|ossuary|cemetery|^rest$|churchyard|^dead$/],
  ['barrow', /barrow|cairn|^mounds?$|^howe$|^tumulus/],
  ['standing_stones', /henge|^stones$|^circle$|menhir|monolith|^ring$|dolmen/],
  ['ruins', /ruin|remnant|^wreck$|^fallen$|^broken$/],
  ['inn', /^inn$|tavern|waystation|hostel|alehouse|^rest$|coaching/],
  ['port', /harbou?r|^port$|^quay|wharf|^docks?$|^landing$/],
  ['windmill', /^mill$|windmill|^mills$/],
  ['farm', /farm|stead$|grange|croft|^barn$|^fold$/],
  ['mine', /^mines?$|delve|quarry|^dig$|^diggings$|^pits?$|^shaft$/],
  ['mountain_pass', /^pass$|saddle|^col$|^gap$|^notch$/],
  ['camp', /^camp$|encampment|bivouac|^muster$|^laager$/],
  ['lair', /lair|^den$|^nest$|^lurk$|^roost$/],
  ['orchard', /orchard/],
  ['tower', /tower|^spire$|^watch|beacon|lighthouse|^steeple$|^turret$/],
  ['castle', /castle|^keep$|^fort|fortress|citadel|stronghold|palace|^hold$|bastion|rampart|garrison|manor|^hall$|^court$/],
  ['cave', /cave|cavern|grotto|undercroft|^warren$|^hollow$|^deeps?$/],
  ['pine_forest', /firs?$|pines?$|spruce|^larch|^yews?$/],
  ['copse', /copse|coppice|grove|^holt$|spinney/],
  ['swamp_wood', /drowned|^carr$|swamp|^mire$|.mire$/],
  ['reeds', /reeds?$|sedge|rushes/],
  ['hills', /^hills?$|^fells?$|^downs$|knoll|^brae$/],
  ['crags', /crag|^tor$|^rocks?$|^scar$|^stack$|^butte$/],
  ['peaks', /^peaks?$|summit|heights?$|^pike$|^ben$/],
  ['mountain', /mount|ridge|cliff/],
];
const KIND_ART: Partial<Record<PlaceKind, string>> = {
  castle: 'castle', tower: 'tower', town: 'village', shrine: 'shrine', ruin: 'ruins', cave: 'cave', road: 'inn', field: 'farm',
  forest: 'deep_forest', bog: 'swamp_wood', lake: 'reeds', peak: 'crags',
};
/** Tiles that stand for a settlement, a holy place or a stronghold (a seat or village can be one of these). */
const SETTLED_ART = new Set(['town', 'village', 'castle', 'port', 'farm', 'windmill', 'inn', 'abbey']);
export function placeArt(name: string, look: PlaceLook, home = false): string | undefined {
  if (look.kind === 'bridge' || look.kind === 'ford') return undefined;
  const named = wordsOf(name).map((w) => ART_WORDS.find(([, re]) => re.test(w))?.[0]).filter((a): a is string => a !== undefined);
  const head = named[named.length - 1];
  if (home) return head === 'castle' || head === 'port' ? head : 'town';
  // a village stands as a village, a port, a farm, a mill, an inn or an abbey, whatever else its name says
  if (look.kind === 'town') return head && SETTLED_ART.has(head) && head !== 'town' && head !== 'castle' ? head : 'village';
  return head ?? KIND_ART[look.kind];
}

/** A seed from the World's seed bytes (falls back to its id). */
export function seedFrom(bytes: Uint8Array | undefined, id: bigint | undefined): number {
  let h = 0x811c9dc5;
  const mix = (v: number) => { h ^= v & 0xff; h = Math.imul(h, 0x01000193); };
  if (bytes && bytes.length) for (const b of bytes) mix(b);
  else for (const c of (id ?? 0n).toString()) mix(c.charCodeAt(0));
  return h >>> 0;
}

/**
 * Node cells a 1400×860 window shows clear of the panels: under the top bar, left of the quest
 * column, and off the band panel (top left) and the dock (bottom left). Keeps and towers rise up to
 * four cells above their node, so they never start in the top rows.
 */
const AREA = { x0: 14, x1: 30, y0: 7, y1: 15 };
const covered = (x: number, y: number) => (x <= 18 && y <= 8) || (x <= 20 && y >= 15);
/** Water-borne kinds stand on the river; the rest keep this far from any water. */
const DRY = 2.3;
const WALL_BASE = { grey: 68, tan: 1151, brick: 239 } as const;
const FACADES = [544, 543] as const;
const DOORS = [204, 207, 203] as const;
const CASTLE_FLOOR = 62;
const CAVE_MOUTH = 216, GATE = 258, TOWER_DOOR = 202;

type Cell = [number, number];
/** A cell as a number for the sets and maps (cells a little off the chart's edge too); `unk` reads one back. */
const k = (x: number, y: number) => (y + 64) * 4096 + (x + 64);
const unk = (c: number): Cell => [(c % 4096) - 64, Math.floor(c / 4096) - 64];

/** A min-heap of [priority, value] for A*. */
class Heap {
  private a: Array<[number, number]> = [];
  get size() { return this.a.length; }
  push(p: number, v: number) {
    const a = this.a; a.push([p, v]);
    let i = a.length - 1;
    while (i > 0) { const j = (i - 1) >> 1; if (a[j]![0] <= a[i]![0]) break; [a[i], a[j]] = [a[j]!, a[i]!]; i = j; }
  }
  pop(): number {
    const a = this.a, top = a[0]!, last = a.pop()!;
    if (a.length) {
      a[0] = last;
      let i = 0;
      for (;;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < a.length && a[l]![0] < a[m]![0]) m = l; if (r < a.length && a[r]![0] < a[m]![0]) m = r; if (m === i) break; [a[i], a[m]] = [a[m]!, a[i]!]; i = m; }
    }
    return top[1];
  }
}

/**
 * How each country's valley is shaped (docs/143 OW3): how wooded (the tree noise's threshold, lower is more
 * wooded), how much of the rest is pines, how many fens and tarns of its own, how many ranges. Undefined
 * (no clock yet) is temperate.
 */
interface CountryShape { wooded: readonly [number, number]; pines: number; bogs: number; tarn: number; massifs: readonly [number, number] }
const SHAPES: Record<Biome, CountryShape> = {
  temperate: { wooded: [0.6, 0.72], pines: 0.68, bogs: 0.35, tarn: 0.45, massifs: [3, 7] },
  wetland: { wooded: [0.58, 0.7], pines: 0.7, bogs: 2.2, tarn: 0.9, massifs: [2, 4] },
  boreal: { wooded: [0.5, 0.62], pines: 0.5, bogs: 0.5, tarn: 0.75, massifs: [3, 7] },
  desert: { wooded: [0.8, 0.86], pines: 0.8, bogs: 0.3, tarn: 0.55, massifs: [3, 6] },
  highland: { wooded: [0.64, 0.74], pines: 0.6, bogs: 0.6, tarn: 0.6, massifs: [5, 9] },
  jungle: { wooded: [0.46, 0.56], pines: 0.72, bogs: 0.7, tarn: 0.45, massifs: [3, 6] },
  underdark: { wooded: [0.56, 0.68], pines: 0.62, bogs: 1.2, tarn: 0.6, massifs: [4, 8] },
  ashlands: { wooded: [0.68, 0.76], pines: 0.7, bogs: 0.6, tarn: 0.5, massifs: [4, 8] },
};

/** Where a region's places may stand: its block less a margin wider than its borders wander (inclusive bounds, as `AREA`). */
const regionArea = (r: OwRegion) => ({ x0: r.x0 + 5, x1: r.x1 - 6, y0: r.y0 + 4, y1: r.y1 - 5 });
/** A search box for the road A* (x1, y1 exclusive). */
interface Box { x0: number; y0: number; x1: number; y1: number }

/**
 * `biome` shapes the valley for its country (docs/143 OW3); every player in a World generates it alike.
 *
 * docs/185 PB13: `regions` with two biomes or more make one continuous map of biome regions (`overworldRegions.ts`
 * lays them out): each place stands in its `biome`'s region (the seat's when it has none), each cell is shaped by its
 * region's country, each region's places are joined by road and the regions by roads between them, rivers run on
 * across borders (into the Ashlands as lava, past a crag where the water meets it), and the Underdark sits sunken
 * behind a rim of cliffs with one way down. A single region, or none, draws exactly the one-country chart. Whether
 * a region is unlocked changes nothing here: the map is the same before and after (only the drawing veils it).
 */
export function generateOverworld(seed: number, sites: readonly MapSite[], opts: { biome?: Biome; regions?: readonly RegionSpec[] } = {}): Overworld {
  const plan = planRegions(seed, opts.regions ?? [], opts.biome);
  const one = plan ? undefined : opts.regions?.[0]?.biome ?? opts.biome;
  const rand = mulberry(seed);
  const shape = SHAPES[one ?? 'temperate'];
  const W = plan?.w ?? OW_W, H = plan?.h ?? OW_H;
  const R = plan?.regions;
  const within = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H;
  /** The region (index) of a cell (0 on a one-country map), off the edge the nearest cell's. */
  const regionOf = (x: number, y: number) => (plan ? plan.at[Math.max(0, Math.min(H - 1, Math.floor(y))) * W + Math.max(0, Math.min(W - 1, Math.floor(x)))]! : 0);
  const shapeOf = (r: number) => (R ? SHAPES[R[r]!.biome] : shape);
  const homeRegion = R ? Math.max(0, R.findIndex((r) => r.home)) : 0;
  const siteRegion = new Map(sites.map((s) => [s.key, R && s.biome ? Math.max(0, R.findIndex((r) => r.biome === s.biome)) : homeRegion]));
  const regionOfKey = (key: string) => siteRegion.get(key) ?? homeRegion;
  const areaOf = (key: string) => (R ? regionArea(R[regionOfKey(key)]!) : AREA);
  const isCovered = (x: number, y: number) => !plan && covered(x, y);
  // the way down to the Underdark's gate stays clear of rock and buildings (the gate's column and its neighbours, a few
  // cells either side of the rim), and no place stands near it
  const gates = R ? R.flatMap((g) => (g.gate ? [g.gate.x] : [])).map((gx) => ({ gx, ys: Array.from({ length: H }, (_, y) => y).filter((y) => plan!.cliff[y * W + gx] === 2) })) : [];
  const nearGate = (x: number, y: number, dx: number, dy: number) => gates.some(({ gx, ys }) => Math.abs(x - gx) <= dx && ys.length > 0 && y >= ys[0]! - dy && y <= ys[ys.length - 1]! + dy);
  const toGate = (x: number, y: number) => nearGate(x, y, 1, 4) && !plan!.cliff[y * W + x];
  /** A region's block grown by `m` cells, kept on the map (the road A*'s search box). */
  const grown = (r: number, m: number): Box => { const g = R![r]!; return { x0: Math.max(0, g.x0 - m), y0: Math.max(0, g.y0 - m), x1: Math.min(W, g.x1 + m), y1: Math.min(H, g.y1 + m) }; };
  const range = (a: number, b: number) => a + rand() * (b - a);
  const looks: Record<string, PlaceLook> = {};
  for (const s of sites) { const l = placeLook(s.name, s.text, s.slot ?? (s.key === 'home' ? 'home' : undefined)); looks[s.key] = s.kind ? { ...l, kind: s.kind, tags: [...new Set([l.kind, ...l.tags])].filter((t) => t !== s.kind && t !== 'field') } : l; }
  const onWater = (key: string) => { const l = looks[key]!; return l.kind === 'bridge' || l.kind === 'ford'; };
  const arts: Record<string, string | undefined> = {};
  for (const s of sites) arts[s.key] = placeArt(s.name, looks[s.key]!, s.key === 'home');

  const water = new Set<number>();
  const mountain = new Set<number>();
  const built = new Set<number>();      // keeps, cottages, towers: roads go round them
  const keep = new Set<number>();       // cells nothing may cover (roads, node squares, labels)
  const fills: MapProp[] = [];
  const props: MapProp[] = [];
  const nodes: Record<string, MapNode> = {};

  // ── 1. places on the water first, then the river through them ──
  const order = [...sites].sort((a, b) => Number(onWater(b.key)) - Number(onWater(a.key)) || (a.key === 'home' ? -1 : b.key === 'home' ? 1 : 0));
  const placed: Cell[] = [];
  const apart = (p: Cell, slack: number) => placed.every(([x, y]) => { const dx = Math.abs(x - p[0]), dy = Math.abs(y - p[1]); return dx >= 7 - slack || (dy >= 4 - Math.min(1, slack) && dx + dy >= 7 - slack); });
  const waterDist = (x: number, y: number) => {
    let best = 99;
    for (let dy = -4; dy <= 4; dy++) for (let dx = -4; dx <= 4; dx++) if (water.has(k(x + dx, y + dy))) best = Math.min(best, Math.hypot(dx, dy));
    return best;
  };
  const place = (key: string, ok: (p: Cell) => boolean) => {
    const l = looks[key]!, A = areaOf(key);
    const y0 = l.kind === 'castle' || l.kind === 'tower' ? A.y0 + 2 : l.kind === 'cave' ? A.y0 + 1 : A.y0;
    // on a map of regions a place, the keep above it and its label stand wholly in its own region, off the Underdark's rim
    const mine = regionOfKey(key);
    const inRegion = (p: Cell) => {
      if (!plan) return true;
      for (let dy = -4; dy <= 2; dy++) for (let dx = -3; dx <= 3; dx++) { const X = p[0] + dx, Y = p[1] + dy; if (within(X, Y) && (regionOf(X, Y) !== mine || plan.cliff[Y * W + X])) return false; }
      return true;
    };
    const fits = (p: Cell) => !isCovered(p[0], p[1]) && !nearGate(p[0], p[1], 5, 7) && inRegion(p);
    for (let slack = 0; slack <= 2; slack++) {
      for (let t = 0; t < 300; t++) {
        const p: Cell = [A.x0 + Math.floor(rand() * (A.x1 - A.x0 + 1)), y0 + Math.floor(rand() * (A.y1 - y0 + 1))];
        if (fits(p) && apart(p, slack) && ok(p)) { placed.push(p); nodes[key] = { x: p[0] + 0.5, y: p[1] + 0.5 }; return; }
      }
    }
    // crowded: the clear cell farthest from every other place. When every clear cell that passes `ok`
    // is on or beside a place already down (a town or keep with no dry ground left round it), the
    // farthest dry cell instead, then the farthest clear one, so two places never stack on one square
    let best: Cell = [A.x0, A.y1], score = -1;
    const river = onWater(key);
    for (const pass of [ok, ([x, y]: Cell) => river || waterDist(x, y) >= DRY, () => true]) {
      for (let y = y0; y <= A.y1; y++) for (let x = A.x0; x <= A.x1; x++) {
        if (!fits([x, y]) || !pass([x, y])) continue;
        const d = Math.min(99, ...placed.map(([px, py]) => Math.abs(px - x) * 0.6 + Math.abs(py - y)));
        if (d > score) { score = d; best = [x, y]; }
      }
      if (score > 1.6) break;
    }
    placed.push(best); nodes[key] = { x: best[0] + 0.5, y: best[1] + 0.5 };
  };
  const wet = order.filter((s) => onWater(s.key));
  /**
   * A river along `vertical` (down the map or across it) from `frame`'s edge to its far edge, through `through` (the
   * water-borne places, in order along it) or else `mid()`, into the cells `flows` lets it.
   */
  const river = (through: MapNode[], vertical: boolean, mid0: () => MapNode, frame: Box, flows: (x: number, y: number) => boolean) => {
    const mid = through.length ? through : [mid0()];
    const first = mid[0]!, last = mid[mid.length - 1]!;
    const pts: Array<[number, number]> = vertical
      ? [[first.x + range(-9, 9), frame.y0 - 1.5], ...mid.map((n) => [n.x, n.y] as [number, number]), [last.x + range(-9, 9), frame.y1 + 1.5]]
      : [[frame.x0 - 1.5, first.y + range(-6, 6)], ...mid.map((n) => [n.x, n.y] as [number, number]), [frame.x1 + 1.5, last.y + range(-6, 6)]];
    const amp = range(0.6, 1.8), freq = range(0.18, 0.42), phase = rand() * Math.PI * 2;
    const half = rand() < 0.3 ? 0.62 : 0.98;   // one cell or two wide
    const pin = (x: number, y: number) => Math.min(1, ...through.map((n) => Math.hypot(n.x - x, n.y - y) / 3.5), 1);
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)]!, p1 = pts[i]!, p2 = pts[i + 1]!, p3 = pts[Math.min(pts.length - 1, i + 2)]!;
      const len = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
      const n = Math.max(2, Math.ceil(len / 0.15));
      for (let j = 0; j <= n; j++) {
        const t = j / n, t2 = t * t, t3 = t2 * t;
        const cx = 0.5 * (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
        const cy = 0.5 * (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
        const wig = amp * Math.sin((vertical ? cy : cx) * freq + phase) * pin(cx, cy);
        const x = vertical ? cx + wig : cx, y = vertical ? cy : cy + wig;
        for (let yy = Math.floor(y - 1.5); yy <= Math.ceil(y + 1.5); yy++) for (let xx = Math.floor(x - 1.5); xx <= Math.ceil(x + 1.5); xx++) {
          if (within(xx, yy) && Math.hypot(xx + 0.5 - x, yy + 0.5 - y) < half + 0.25 && flows(xx, yy)) water.add(k(xx, yy));
        }
      }
    }
    for (const n of through) water.add(k(Math.floor(n.x), Math.floor(n.y)));
  };
  /** Each region's river axis (the one-country map's at 0), for the crossings' bridges. */
  const verticalOf: boolean[] = [];
  if (!R) {
    // the river's axis: down the map or across it
    const vertical = rand() < 0.55;
    verticalOf.push(vertical);
    for (const s of wet) place(s.key, () => true);
    const riverSide = order.some((s) => !onWater(s.key) && looks[s.key]!.river);
    if (wet.length || riverSide || rand() < 0.55) {
      // control points: in at one edge, through every water-borne place in turn, out at the other
      const along = (n: MapNode) => (vertical ? n.y : n.x);
      const through = wet.map((s) => nodes[s.key]!).sort((a, b) => along(a) - along(b));
      river(through, vertical, () => (vertical ? { x: range(10, 34), y: H / 2 } : { x: W / 2, y: range(7, 19) }), { x0: 0, y0: 0, x1: W, y1: H }, () => true);
    }
  } else {
    // PB13: a region's river runs on from map edge to map edge through the regions in its way (lava in the
    // Ashlands); a region some river already crosses gets one of its own only for its water-borne places. The
    // Underdark's stays under its rock, and no surface river falls into it (it stops at the cliff).
    for (const s of wet) place(s.key, () => true);
    const crossed = new Set<number>();
    const twoRows = R.some((g) => g.y0 > 0);
    R.forEach((g, r) => {
      const deep = g.biome === 'underdark';
      // down the map most often, and always on a map of two rows: a river across it would cross every region in its row
      const vertical = rand() < (deep ? 0.55 : twoRows ? 1 : 0.7);
      verticalOf.push(vertical);
      const mine = wet.filter((s) => regionOfKey(s.key) === r);
      const riverSide = order.some((s) => !onWater(s.key) && looks[s.key]!.river && regionOfKey(s.key) === r);
      if (!(mine.length || riverSide || (!crossed.has(r) && rand() < 0.55))) return;
      const along = (n: MapNode) => (vertical ? n.y : n.x);
      const through = mine.map((s) => nodes[s.key]!).sort((a, b) => along(a) - along(b));
      const A = regionArea(g);
      const frame: Box = deep ? { x0: g.x0, y0: g.y0, x1: g.x1, y1: g.y1 } : vertical ? { x0: g.x0, y0: 0, x1: g.x1, y1: H } : { x0: 0, y0: g.y0, x1: W, y1: g.y1 };
      const before = water.size;
      river(through, vertical, () => (vertical ? { x: range(A.x0, A.x1), y: (g.y0 + g.y1) / 2 } : { x: (g.x0 + g.x1) / 2, y: range(A.y0, A.y1) }), frame,
        (x, y) => (R[regionOf(x, y)]!.biome === 'underdark') === deep && !plan.cliff[y * W + x]);
      if (water.size > before) for (const c of water) { const [x, y] = unk(c); crossed.add(regionOf(x, y)); }
    });
  }

  // ── 2. the rest of the places: dry ground, riverside ones near the water ──
  for (const s of order) {
    if (onWater(s.key)) continue;
    const l = looks[s.key]!;
    const port = arts[s.key] === 'port' && water.size > 0;
    place(s.key, ([x, y]) => {
      const d = waterDist(x, y);
      // a port stands on the bank, its quay in the water (docs/143 OW2)
      if (port) return d >= 1 && d < 1.5 && !water.has(k(x, y - 1));
      if (d < DRY) return false;
      // keeps and cottages above the node need dry ground too
      if (l.kind === 'castle' || l.kind === 'town' || l.kind === 'tower') for (let dy = -4; dy <= 0; dy++) for (let dx = -3; dx <= 3; dx++) if (water.has(k(x + dx, y + dy))) return false;
      return l.river && water.size ? d <= 3.6 : true;
    });
  }
  const keys = order.map((s) => s.key);
  const cellOf = (key: string): Cell => [Math.floor(nodes[key]!.x), Math.floor(nodes[key]!.y)];
  // each node's square, its label below and a cell of air either side stay clear
  for (const key of keys) {
    const [x, y] = cellOf(key);
    for (let dy = 0; dy <= 1; dy++) for (let dx = -2; dx <= 2; dx++) keep.add(k(x + dx, y + dy));
    keep.add(k(x, y - 1));
  }
  const near = (x: number, y: number, r: number) => keys.some((key) => { const [nx, ny] = cellOf(key); return Math.hypot(nx - x, ny - y) < r; });

  // ── 3. lakes: one off each lake's shore, sometimes a tarn of the seed's own ──
  // on a map of regions a lake stays in its own region, off the Underdark's rim
  const lake = (cx: number, cy: number, rx: number, ry: number, spare?: string, region?: number) => {
    const ln = noise(rand, W, H, 2);
    for (let y = Math.floor(cy - ry - 2); y <= cy + ry + 2; y++) for (let x = Math.floor(cx - rx - 2); x <= cx + rx + 2; x++) {
      if (!within(x, y) || keep.has(k(x, y))) continue;
      if (plan && (regionOf(x, y) !== region || plan.cliff[y * W + x])) continue;
      if (keys.some((key) => key !== spare && Math.hypot(cellOf(key)[0] - x, cellOf(key)[1] - y) < 3)) continue;
      if (((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 + (ln(x, y) - 0.5) * 0.8 < 1) water.add(k(x, y));
    }
  };
  for (const key of keys) {
    if (looks[key]!.kind !== 'lake') continue;
    const [x, y] = cellOf(key);
    const dirs: Cell[] = [[0, -1], [1, 0], [-1, 0], [0, 1]];
    const start = Math.floor(rand() * 4);
    const pick = dirs.map((_, i) => dirs[(start + i) % 4]!).find(([dx, dy]) => !near(x + dx * 4, y + dy * 4 - (dy < 0 ? 1 : 0), 3.2) || false) ?? dirs[start]!;
    const rx = range(2.6, 3.8), ry = range(1.8, 2.6);
    lake(x + 0.5 + pick[0] * (rx + 0.9), y + 0.5 + pick[1] * (ry + 0.9), rx, ry, key, regionOfKey(key));
  }
  // each region's own tarn, now and then (the one-country map's over the whole chart)
  for (let r = 0; r < (R?.length ?? 1); r++) {
    const g = R?.[r], b = g ? { x0: g.x0, y0: g.y0, x1: g.x1, y1: g.y1 } : { x0: 0, y0: 0, x1: W, y1: H };
    if (rand() < shapeOf(r).tarn) {
      for (let t = 0; t < 40; t++) {
        const cx = range(b.x0 + 2, b.x1 - 2), cy = range(b.y0 + 2, b.y1 - 2);
        if (!near(cx, cy, 6.5)) { lake(cx, cy, range(1.8, 3.2), range(1.4, 2.2), undefined, r); break; }
      }
    }
  }
  // PB13: where a river runs on into the Ashlands, the water meets the lava at a black crag (its last cell of lava
  // cooled to rock), and the river goes on beyond it as lava; nowhere do the two touch
  const crust = new Set<number>();
  if (R) {
    const ash = (x: number, y: number) => R[regionOf(x, y)]!.biome === 'ashlands';
    for (const c of water) {
      const [x, y] = unk(c);
      if (!ash(x, y)) continue;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) if (water.has(k(x + dx, y + dy)) && !ash(x + dx, y + dy)) crust.add(c);
    }
    for (const c of crust) water.delete(c);
  }

  // ── 4. ranges: behind peaks and caves, and the seed's own massifs out in the wild ──
  const mnoise = noise(rand, W, H, 3);
  const massifs: Array<[number, number, number, number]> = [];
  for (const key of keys) {
    const l = looks[key]!, [x, y] = cellOf(key);
    if (l.kind === 'peak' || l.kind === 'cave') massifs.push([x + 0.5 + range(-1, 1), y - 2.4, range(3.2, 4.6), range(1.8, 2.4)]);
    else if (l.tags.includes('peak')) massifs.push([x + 0.5 + (rand() < 0.5 ? -4 : 4), y - 1.5, range(2, 3), range(1.4, 2)]);
  }
  // each region's own ranges, by its country (the one-country map's over the whole chart)
  for (let r = 0; r < (R?.length ?? 1); r++) {
    const sh = shapeOf(r), g = R?.[r], b = g ? { x0: g.x0, y0: g.y0, x1: g.x1, y1: g.y1 } : { x0: 0, y0: 0, x1: W, y1: H };
    const extra = sh.massifs[0] + Math.floor(rand() * (sh.massifs[1] - sh.massifs[0] + 1));
    for (let i = 0, t = 0; i < extra && t < 200; t++) {
      const cx = range(b.x0 + 1, b.x1 - 1), cy = range(b.y0 + 1, b.y1 - 1);
      if (near(cx, cy, 5.5)) continue;
      massifs.push([cx, cy, range(2, 5), range(1.6, 3.4)]);
      i++;
    }
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (water.has(k(x, y)) || keep.has(k(x, y)) || toGate(x, y)) continue;
    for (const [cx, cy, rx, ry] of massifs) {
      const d = ((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2;
      if (d + (mnoise(x, y) - 0.5) * 0.9 < 1 && !near(x, y, 2.2)) { mountain.add(k(x, y)); break; }
    }
  }
  // PB13: the crags where water meets lava, and the Underdark's rim of cliffs (its gate a pass the entrance road climbs)
  for (const c of crust) mountain.add(c);
  if (plan) for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (plan.cliff[y * W + x] && !keep.has(k(x, y))) { water.delete(k(x, y)); mountain.add(k(x, y)); }
  // a cave's mouth opens in the rock right above its node
  for (const key of keys) {
    if (looks[key]!.kind !== 'cave') continue;
    const [x, y] = cellOf(key);
    for (let dx = -1; dx <= 1; dx++) for (let dy = -3; dy <= -2; dy++) if (!water.has(k(x + dx, y + dy))) mountain.add(k(x + dx, y + dy));
    mountain.delete(k(x, y - 1));
  }

  // ── 5. what stands at each place ──
  const wallPieces = (cells: Cell[], base: number) => {
    const s = new Set(cells.map(([x, y]) => k(x, y)));
    return cells.map(([x, y]) => {
      const m = (s.has(k(x, y - 1)) ? NORTH : 0) | (s.has(k(x + 1, y)) ? EAST : 0) | (s.has(k(x, y + 1)) ? SOUTH : 0) | (s.has(k(x - 1, y)) ? WEST : 0);
      return { frame: base + (WALL16[m] ?? 0), x, y };
    });
  };
  const build = (cells: MapProp[]) => { for (const c of cells) if (within(c.x, c.y) && !toGate(c.x, c.y)) { props.push(c); built.add(k(c.x, c.y)); mountain.delete(k(c.x, c.y)); } };
  const hashAt = (x: number, y: number) => (Math.imul(x + 1, 73856093) ^ Math.imul(y + 1, 19349663) ^ seed) >>> 0;
  const forestBoost: Array<[number, number, number]> = [];   // x, y, strength
  const bogAt: Array<[number, number, number, number]> = [];  // x, y, rx, ry
  const cottage = (x: number, y: number) => {
    build(wallPieces([[x, y - 1], [x + 1, y - 1]], WALL_BASE.brick));
    build([{ frame: FACADES[hashAt(x, y) % FACADES.length]!, x, y }, { frame: DOORS[hashAt(x, y + 1) % DOORS.length]!, x: x + 1, y }]);
  };
  const free = (x: number, y: number, w = 1, h = 1) => {
    for (let dy = 0; dy < h; dy++) for (let dx = 0; dx < w; dx++) { const c = k(x + dx, y + dy); if (!within(x + dx, y + dy) || water.has(c) || keep.has(c) || built.has(c) || toGate(x + dx, y + dy)) return false; }
    return true;
  };
  for (const key of keys) {
    const l = looks[key]!, [x, y] = cellOf(key);
    switch (l.kind) {
      case 'castle': {
        // a walled keep: back wall, sides round a dark court, the front with its gate over the node, a tower
        const ring: Cell[] = [];
        for (let dx = -2; dx <= 2; dx++) { ring.push([x + dx, y - 3]); ring.push([x + dx, y - 1]); }
        ring.push([x - 2, y - 2], [x + 2, y - 2], [x, y - 4]);
        if (rand() < 0.5) ring.push([x - 2, y - 4], [x + 2, y - 4]);
        const walls = wallPieces(ring, WALL_BASE.grey).map((p) => (p.x === x && p.y === y - 1 ? { ...p, frame: GATE } : p));
        for (let dx = -1; dx <= 1; dx++) { fills.push({ frame: CASTLE_FLOOR, x: x + dx, y: y - 2 }); built.add(k(x + dx, y - 2)); }
        build(walls);
        break;
      }
      case 'tower': {
        const tall = rand() < 0.5 ? 3 : 2;
        const cells: Cell[] = Array.from({ length: tall }, (_, i) => [x, y - 1 - i] as Cell);
        build(wallPieces(cells, WALL_BASE.grey).map((p) => (p.y === y - 1 ? { ...p, frame: TOWER_DOOR } : p)));
        break;
      }
      case 'town': {
        // cottages round the node, a well among them, a fence along the front
        const spots: Cell[] = [[-4, -1], [2, -1], [-2, -3], [1, -3], [-5, -3], [3, -3], [-4, 2], [3, 2]];
        const want = 2 + Math.floor(rand() * 3);
        let made = 0;
        for (const i of spots.map((_, j) => j).sort(() => rand() - 0.5)) {
          const [dx, dy] = spots[i]!;
          if (made < want && free(x + dx, y + dy - 1, 2, 2)) { cottage(x + dx, y + dy); made++; }
        }
        if (free(x + 2, y)) build([{ frame: PROPS.well, x: x + 2, y }]);
        else if (free(x - 2, y)) build([{ frame: PROPS.well, x: x - 2, y }]);
        if (free(x - 1, y - 2) && rand() < 0.6) build([{ frame: PROPS.barrel[0], x: x - 1, y: y - 2 }]);
        break;
      }
      case 'shrine': {
        build([{ frame: rand() < 0.5 ? PROPS.pedestals.fountain : PROPS.stoneRing, x, y: y - 1 }]);
        for (const dx of [-1, 1]) if (free(x + dx, y - 1)) build([{ frame: PROPS.statues.hooded, x: x + dx, y: y - 1 }]);
        for (let dx = -1; dx <= 1; dx++) fills.push({ frame: 59, x: x + dx, y: y - 1 });
        break;
      }
      case 'ruin': {
        const bits = [PROPS.tombstone, PROPS.tombstoneBroken, PROPS.rubble, WALL_BASE.grey + 16, WALL_BASE.grey + 17, PROPS.statues.lying, PROPS.bones[0]];
        for (let t = 0, n = 0; t < 30 && n < 5; t++) {
          const dx = Math.round(range(-3, 3)), dy = Math.round(range(-3, 0));
          if (Math.abs(dx) + Math.abs(dy) >= 1 && free(x + dx, y + dy)) { build([{ frame: bits[Math.floor(rand() * bits.length)]!, x: x + dx, y: y + dy }]); n++; }
        }
        break;
      }
      case 'cave': build([{ frame: CAVE_MOUTH, x, y: y - 1 }]); break;
      case 'road': {
        if (free(x - 1, y - 1)) build([{ frame: PROPS.barrel[0], x: x - 1, y: y - 1 }]);
        if (free(x + 1, y - 1) && rand() < 0.6) build([{ frame: PROPS.bucket[0], x: x + 1, y: y - 1 }]);
        break;
      }
      case 'field': {
        for (let i = -2; i <= 2; i++) if (free(x + i, y - 2)) props.push({ frame: i === -2 || i === 2 ? FENCE_WOOD.post : FENCE_WOOD.ew[(i + 2) % 4]!, x: x + i, y: y - 2 });
        break;
      }
      case 'forest': forestBoost.push([x + 0.5, y + 0.5, 0.8]); break;
      case 'bog': bogAt.push([x + 0.5, y + 1.2, range(3.6, 5), range(2.2, 3)]); break;
      default: break;
    }
    if (l.tags.includes('forest')) forestBoost.push([x + 0.5, y + 0.5, 0.26]);
    if (l.tags.includes('bog') && l.kind !== 'bog') bogAt.push([x + 0.5, y + 1, range(2.4, 3.4), range(1.6, 2.2)]);
  }

  // the country's own fens and rifts, out in the wild (docs/143 OW3): `bogs` is how many, the fraction a chance of one more
  for (let r = 0; r < (R?.length ?? 1); r++) {
    const sh = shapeOf(r), g = R?.[r], b = g ? { x0: g.x0, y0: g.y0, x1: g.x1, y1: g.y1 } : { x0: 0, y0: 0, x1: W, y1: H };
    for (let n = Math.floor(sh.bogs) + (rand() < sh.bogs % 1 ? 1 : 0), t = 0; n > 0 && t < 60; t++) {
      const cx = range(b.x0 + 3, b.x1 - 3), cy = range(b.y0 + 3, b.y1 - 3);
      if (near(cx, cy, 5) || water.has(k(Math.floor(cx), Math.floor(cy))) || mountain.has(k(Math.floor(cx), Math.floor(cy)))) continue;
      bogAt.push([cx, cy, range(2, 3.4), range(1.4, 2.2)]);
      n--;
    }
  }

  // ── 6. the woods and the mud, planned before the roads so the roads can go round them ──
  const tnoise = noise(rand, W, H, 3 + Math.floor(rand() * 3)), pnoise = noise(rand, W, H, 3);
  // lower is a more wooded valley; on a map of regions each region's own
  const woodedOf = Array.from({ length: R?.length ?? 1 }, (_, r) => range(shapeOf(r).wooded[0], shapeOf(r).wooded[1]));
  const woodedAt = (x: number, y: number) => woodedOf[regionOf(x, y)]!;
  const bnoise = noise(rand, W, H, 2);
  const bog = new Set<number>(), autumn = new Set<number>();
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const c = k(x, y);
    if (water.has(c) || mountain.has(c) || built.has(c)) continue;
    let best = 9;
    for (const [cx, cy, rx, ry] of bogAt) best = Math.min(best, ((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 + (bnoise(x, y) - 0.5) * 0.7);
    if (best < 1) bog.add(c);
    else if (best < 2.4 && bnoise(x + 40, y) > 0.42 && !keep.has(c)) autumn.add(c);
  }
  // in the desert the groves crowd the water and thin out away from it (docs/143 OW3)
  const dry = (i: number) => (R ? R[plan.at[i]!]!.biome === 'desert' : one === 'desert');
  const thirst = (R ? R.some((g) => g.biome === 'desert') : one === 'desert') ? Array.from({ length: W * H }, (_, i) => (dry(i) ? Math.max(0, Math.min(0.3, (waterDist(i % W, Math.floor(i / W)) - 2) * 0.1)) : 0)) : undefined;
  const treeLevel = (x: number, y: number) => {
    let v = tnoise(x, y) + (y < 2 || y > H - 3 ? 0.06 : 0) - (thirst?.[y * W + x] ?? 0);
    for (const [cx, cy, s] of forestBoost) { const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy); if (d > 1.4 && d < 6.5) v += s * (1 - Math.max(0, d - 4.5) / 2); }
    return v;
  };

  // ── 7. roads: a random spanning tree (and sometimes a loop) of A* paths ──
  const crossAt = new Set<number>();   // water cells a bridge or ford stands over
  for (const key of keys) if (onWater(key)) { const [x, y] = cellOf(key); for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) crossAt.add(k(x + dx, y + dy)); }
  // docs/143 OW5: a crossing's bridge runs across its water, so every road meets it end on: east–west over
  // a river running north–south, north–south over one running east–west
  const crossings: Record<string, 'ns' | 'ew'> = {};
  for (const key of keys) {
    if (!onWater(key)) continue;
    const [x, y] = cellOf(key);
    let down = 0, across = 0;
    for (let d = -3; d <= 3; d++) { if (water.has(k(x, y + d))) down++; if (water.has(k(x + d, y))) across++; }
    crossings[key] = down > across ? 'ew' : down < across ? 'ns' : verticalOf[regionOfKey(key)] ?? true ? 'ew' : 'ns';
  }
  const crossingAxis = new Map(Object.entries(crossings).map(([key, a]) => [k(...cellOf(key)), a]));
  // docs/143 OW4: a road may climb over a thin ridge (one or two cells of rock, north–south) by a mountain pass
  const ridge = (x: number, y: number) => {
    if (!mountain.has(k(x, y))) return 0;
    let n = 1;
    for (let d = 1; mountain.has(k(x, y - d)); d++) n++;
    for (let d = 1; mountain.has(k(x, y + d)); d++) n++;
    return n;
  };
  const road = new Set<number>();
  const nodeCells = new Map(keys.map((key) => [k(...cellOf(key)), key]));
  // what a step onto open ground costs, and which rock a road may climb, worked out once
  const ground = new Float64Array(W * H), climb = new Uint8Array(W * H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const c = k(x, y);
    ground[y * W + x] = 1 + (keep.has(c) ? 5 : 0) + (bog.has(c) ? 0.8 : 0) + Math.max(0, treeLevel(x, y) - woodedAt(x, y) + 0.1) * 6;
    // the Underdark's rim is climbed only at its gate, however thick it is there
    climb[y * W + x] = plan?.cliff[y * W + x] ? (plan.cliff[y * W + x] === 2 ? 1 : 0) : ridge(x, y) > 0 && ridge(x, y) <= 2 ? 1 : 0;
  }
  const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]] as const;
  /**
   * A* over (cell, heading): roads run straight over water and through passes, and turn only on dry ground. `box`
   * bounds the search (a map of regions is large; a region's roads stay near it); the whole chart by default.
   */
  const astar = (from: Cell, to: Cell, ends: Set<number>, heading?: number, box: Box = { x0: 0, y0: 0, x1: W, y1: H }): Cell[] | undefined => {
    const inBox = (x: number, y: number) => x >= box.x0 && y >= box.y0 && x < box.x1 && y < box.y1;
    // each cell's step cost once per call (the roads laid so far are cheap to follow); rock only north–south
    const step = new Float64Array(W * H), rock = new Uint8Array(W * H), axisOf = new Int8Array(W * H), straight = new Uint8Array(W * H);
    for (let y = box.y0; y < box.y1; y++) for (let x = box.x0; x < box.x1; x++) {
      const c = k(x, y), i = y * W + x, ax = crossingAxis.get(c);
      axisOf[i] = ax === 'ns' ? 1 : ax === 'ew' ? 2 : 0;
      straight[i] = water.has(c) || mountain.has(c) ? 1 : 0;
      if (ends.has(c)) step[i] = 1;
      // a road may run on over a crossing (along its bridge), never through another place
      else if (ax) step[i] = 1.2;
      else if (built.has(c) || nodeCells.has(c)) step[i] = Infinity;
      else if (mountain.has(c)) { step[i] = climb[i] ? 9 : Infinity; rock[i] = 1; }
      else if (water.has(c)) step[i] = crossAt.has(c) ? 1.2 : 45;
      else if (road.has(c)) step[i] = 0.35;
      else step[i] = ground[i]!;   // `keep` only grows by road cells, which cost less above
    }
    const cost = (x: number, y: number, dy: number): number => (rock[y * W + x] && dy === 0 ? Infinity : step[y * W + x]!);
    const S = W * H * 4;
    const g = new Float64Array(S).fill(Infinity), came = new Int32Array(S).fill(-1);
    const heap = new Heap();
    const fromC = k(...from), fromAxis = crossingAxis.get(fromC);
    for (let d = 0; d < 4; d++) {
      if ((fromAxis && (fromAxis === 'ew') !== (DIRS[d]![0] !== 0)) || (heading !== undefined && d !== heading)) continue;
      const s0 = (from[1] * W + from[0]) * 4 + d;
      g[s0] = 0; heap.push(0, s0);
    }
    let goal = -1;
    while (heap.size) {
      const cur = heap.pop();
      const ci = cur >> 2, cd = cur & 3, cx = ci % W, cy = Math.floor(ci / W);
      if (cx === to[0] && cy === to[1]) { goal = cur; break; }
      const started = came[cur]! < 0 && cx === from[0] && cy === from[1];
      for (let d = 0; d < 4; d++) {
        const [dx, dy] = DIRS[d]!;
        // the first step from a crossing goes along its axis; off water and rock any way but back
        if (!started && d !== cd && (straight[ci] || axisOf[ci])) continue;
        if (started && ((fromAxis && (fromAxis === 'ew') !== (dx !== 0)) || (heading !== undefined && d !== heading))) continue;
        const nx = cx + dx, ny = cy + dy;
        if (!inBox(nx, ny)) continue;
        const nAxis = axisOf[ny * W + nx];
        if (nAxis && (nAxis === 2) !== (dx !== 0)) continue;
        const c = cost(nx, ny, dy);
        if (c === Infinity) continue;
        // a turn costs a little, so roads run straight and bend at elbows
        const turn = !started && d !== cd ? 0.25 : 0;
        const ng = g[cur]! + c + turn;
        const ns = (ny * W + nx) * 4 + d;
        if (ng < g[ns]!) { g[ns] = ng; came[ns] = cur; heap.push(ng + (Math.abs(nx - to[0]) + Math.abs(ny - to[1])) * 0.35, ns); }
      }
    }
    if (goal < 0) return undefined;
    const out: Cell[] = [];
    for (let c = goal; c >= 0; c = came[c]!) out.push([(c >> 2) % W, Math.floor((c >> 2) / W)]);
    return out.reverse();
  };
  const polyline = (cells: Cell[]): Array<[number, number]> => {
    const pts = cells.map(([x, y]) => [x + 0.5, y + 0.5] as [number, number]);
    return pts.filter((p, i) => i === 0 || i === pts.length - 1 || !((pts[i - 1]![0] === p[0] && p[0] === pts[i + 1]![0]) || (pts[i - 1]![1] === p[1] && p[1] === pts[i + 1]![1])));
  };
  const roads: MapRoad[] = [];
  const lay = (a: string, b: string, from: Cell, to: Cell, heading?: number, box?: Box) => {
    const cells = astar(from, to, new Set([k(...from), k(...to)]), heading, box);
    if (!cells) return false;
    for (const [x, y] of cells) { road.add(k(x, y)); keep.add(k(x, y)); }
    roads.push({ a, b, path: polyline(cells) });
    return true;
  };
  const degree = (key: string) => roads.filter((r) => r.a === key || r.b === key).length;
  // a random spanning tree of each region's places (and sometimes a loop); the one-country map's over all of them
  for (let r = 0; r < (R?.length ?? 1); r++) {
    const group = R ? keys.filter((key) => regionOfKey(key) === r) : keys;
    const box = R ? grown(r, 3) : undefined;
    const pairs: Array<[string, string, number]> = [];
    for (let i = 0; i < group.length; i++) for (let j = i + 1; j < group.length; j++) {
      const a = nodes[group[i]!]!, b = nodes[group[j]!]!;
      pairs.push([group[i]!, group[j]!, Math.hypot(a.x - b.x, a.y - b.y) * range(0.7, 1.3)]);
    }
    pairs.sort((p, q) => p[2] - q[2]);
    const root = new Map(group.map((key) => [key, key]));
    const find = (x: string): string => (root.get(x) === x ? x : find(root.get(x)!));
    const unused: Array<[string, string, number]> = [];
    for (const [a, b, w] of pairs) {
      if (find(a) === find(b)) { unused.push([a, b, w]); continue; }
      if (lay(a, b, cellOf(a), cellOf(b), undefined, box)) root.set(find(a), find(b));
      else unused.push([a, b, w]);
    }
    if (group.length >= 4 && rand() < 0.55) {
      const loop = unused.find(([a, b]) => degree(a) <= 1 || degree(b) <= 1) ?? unused[0];
      if (loop) lay(loop[0], loop[1], cellOf(loop[0]), cellOf(loop[1]), undefined, box);
    }
  }
  // PB13: roads between the regions, a random spanning tree over the borders they share (and sometimes a loop),
  // each from the place of one nearest the other; the Underdark's one road comes down through its gate
  if (R && plan) {
    const ud = R.findIndex((g) => g.biome === 'underdark');
    const links: Array<[number, number]> = [];
    for (let a = 0; a < R.length; a++) for (let b = a + 1; b < R.length; b++) {
      const A = R[a]!, B = R[b]!;
      if (a === ud || b === ud) { if (A.gate?.from === b || B.gate?.from === a) links.push([a, b]); continue; }
      const side = A.y0 === B.y0 && (A.x1 === B.x0 || B.x1 === A.x0);
      const stacked = (A.y1 === B.y0 || B.y1 === A.y0) && Math.min(A.x1, B.x1) - Math.max(A.x0, B.x0) >= 12;
      if (side || stacked) links.push([a, b]);
    }
    const order2 = links.map((l) => [l, rand()] as const).sort((p, q) => p[1] - q[1]).map(([l]) => l);
    const root = R.map((_, i) => i);
    const find = (x: number): number => (root[x] === x ? x : find(root[x]!));
    const spare: Array<[number, number]> = [];
    const join = (a: number, b: number) => {
      const ka = keys.filter((key) => regionOfKey(key) === a), kb = keys.filter((key) => regionOfKey(key) === b);
      // the Underdark's road runs by its gate; any other by the shortest way across the border
      const gate = R[a]!.gate ?? R[b]!.gate;
      const at: [number, number] | undefined = gate ? [gate.x + 0.5, R[ud]!.y0 + 0.5] : undefined;
      const tries: Array<[string, string, number]> = [];
      for (const p of ka) for (const q of kb) {
        const P = nodes[p]!, Q = nodes[q]!;
        tries.push([p, q, at ? Math.hypot(P.x - at[0], P.y - at[1]) + Math.hypot(Q.x - at[0], Q.y - at[1]) : Math.hypot(P.x - Q.x, P.y - Q.y)]);
      }
      tries.sort((p, q) => p[2] - q[2]);
      const ga = R[a]!, gb = R[b]!;
      const box: Box = { x0: Math.max(0, Math.min(ga.x0, gb.x0) - 2), y0: Math.max(0, Math.min(ga.y0, gb.y0) - 2), x1: Math.min(W, Math.max(ga.x1, gb.x1) + 2), y1: Math.min(H, Math.max(ga.y1, gb.y1) + 2) };
      return tries.slice(0, 4).some(([p, q]) => lay(p, q, cellOf(p), cellOf(q), undefined, box));
    };
    for (const [a, b] of order2) {
      if (find(a) === find(b)) { if (a !== ud && b !== ud) spare.push([a, b]); continue; }
      if (join(a, b)) root[find(a)] = find(b);
    }
    if (R.length >= 4 && spare.length && rand() < 0.5) join(...spare[Math.floor(rand() * spare.length)]!);
  }
  // a lonely road runs on off the chart (on a map of regions, off its own region's side of the map)
  for (const key of keys) {
    if (looks[key]!.kind !== 'road') continue;
    const [x, y] = cellOf(key);
    let edges: Cell[] = [[x, 0], [x, H - 1], [0, y], [W - 1, y]];
    if (R) edges = edges.filter(([ex, ey]) => regionOf(ex, ey) === regionOfKey(key) && !plan?.cliff[ey * W + ex]);
    const sorted = edges.sort((p, q) => Math.hypot(p[0] - x, p[1] - y) - Math.hypot(q[0] - x, q[1] - y));
    const end = sorted[Math.floor(rand() * 2)] ?? sorted[0];
    if (end) lay(key, `edge:${key}`, [x, y], end, undefined, R ? grown(regionOfKey(key), 2) : undefined);
  }
  // docs/143 OW5: now and then an old drove road crosses the whole valley, edge to edge, meeting the roads it crosses
  // (not on a map of regions: the roads between them are its ways across)
  if (!R && rand() < 0.5) {
    const across = rand() < 0.5;
    const a = Math.floor(range(4, (across ? H : W) - 4)), b = Math.floor(range(4, (across ? H : W) - 4));
    const from: Cell = across ? [0, a] : [a, 0], to: Cell = across ? [W - 1, b] : [b, H - 1];
    if (!nodeCells.has(k(...from)) && !nodeCells.has(k(...to)) && !mountain.has(k(...from)) && !mountain.has(k(...to)) && !water.has(k(...from))) {
      const cells = astar(from, to, new Set([k(...from), k(...to)]));
      if (cells) { for (const [x, y] of cells) { road.add(k(x, y)); keep.add(k(x, y)); } roads.push({ a: 'edge:drove', b: 'edge:drove', path: polyline(cells) }); }
    }
  }
  // docs/143 OW5: a crossing leads somewhere on both banks; one whose roads all reach it from one side runs on
  // across the water and away off the chart, so its bridge never stops mid-river
  for (const key of keys) {
    const axis = crossings[key];
    if (!axis) continue;
    const [x, y] = cellOf(key);
    const sides = new Set<number>();
    for (const r of roads) {
      const pts = r.a === key ? r.path : r.b === key ? [...r.path].reverse() : undefined;
      if (!pts || pts.length < 2) continue;
      sides.add(axis === 'ew' ? Math.sign(pts[1]![0] - pts[0]![0]) : Math.sign(pts[1]![1] - pts[0]![1]));
    }
    if (sides.size >= 2) continue;
    const go = sides.has(1) ? -1 : sides.has(-1) ? 1 : (rand() < 0.5 ? -1 : 1);
    const end: Cell = axis === 'ew' ? [go < 0 ? 0 : W - 1, y] : [x, go < 0 ? 0 : H - 1];
    // on a map of regions the search keeps to a band along the way it runs
    const band: Box | undefined = R ? (axis === 'ew' ? { x0: 0, y0: Math.max(0, y - 5), x1: W, y1: Math.min(H, y + 6) } : { x0: Math.max(0, x - 5), y0: 0, x1: Math.min(W, x + 6), y1: H }) : undefined;
    lay(key, `edge:${key}:far`, [x, y], end, DIRS.findIndex(([dx, dy]) => (axis === 'ew' ? dx === go : dy === go)), band);
  }

  // ── 8. decks and stepping stones where the roads meet water ──
  const stoneBridge = keys.some((key) => onWater(key) && looks[key]!.stone);
  const grid: string[][] = Array.from({ length: H }, (_, y) => Array.from({ length: W }, (_, x) => (water.has(k(x, y)) ? '~' : '.')));
  for (const r of roads) {
    for (let i = 0; i < r.path.length - 1; i++) {
      const [ax, ay] = r.path[i]!, [bx, by] = r.path[i + 1]!;
      const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
      for (let s = 0; s <= n; s++) {
        const x = Math.floor(ax + ((bx - ax) * s) / Math.max(1, n)), y = Math.floor(ay + ((by - ay) * s) / Math.max(1, n));
        const c = k(x, y);
        if (!water.has(c) || built.has(c)) continue;
        built.add(c);
        const fordHere = keys.some((key) => looks[key]!.kind === 'ford' && Math.hypot(cellOf(key)[0] - x, cellOf(key)[1] - y) < 2.5);
        const frame = fordHere ? LIQUID.water!.base + LIQUID_STONE[(x + y) % 2]! : stoneBridge ? 59 : by === ay ? PLANKS.ew[x % 3]! : PLANKS.ns[y % 3]!;
        props.push({ frame, x, y });
      }
    }
  }

  // ── 9. trees, pines, the bog's mud and a gold ring of trees round it ──
  const pines = new Set<number>();
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const c = k(x, y);
    if (water.has(c) || keep.has(c) || mountain.has(c) || built.has(c) || bog.has(c) || autumn.has(c)) continue;
    if (treeLevel(x, y) > woodedAt(x, y) && hashAt(x, y) % 10 !== 0) grid[y]![x] = 'T';
    else if (pnoise(x, y) > shapeOf(regionOf(x, y)).pines && hashAt(x, y) % 4 !== 0) pines.add(c);
  }
  for (const c of bog) {
    const [x, y] = unk(c);
    fills.push({ frame: hashAt(x, y) % 7 === 0 ? FILLS.brownTex : MUD, x, y });
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const c = k(x, y);
    if (built.has(c)) continue;
    if (mountain.has(c)) {
      const l = mountain.has(k(x - 1, y)), r = mountain.has(k(x + 1, y));
      props.push({ frame: !l && r ? 675 : l && !r ? 677 : !l && !r ? (hashAt(x, y) % 2 ? 618 : 620) : 676, x, y });
    } else if (bog.has(c) && !keep.has(c)) {
      const h = hashAt(x, y) % 100;
      if (h < 22) props.push({ frame: PROPS.rocksBrown[h % PROPS.rocksBrown.length]!, x, y });
      else if (h < 32) props.push({ frame: PROPS.bushSmall[h % 3]!, x, y });
      else if (h < 36) props.push({ frame: PROPS.trees.dead, x, y });
    } else if (autumn.has(c)) {
      const h = hashAt(x, y) % 9;
      props.push({ frame: h < 6 ? PROPS.trees.autumnSingle[h % 3]! : h < 8 ? PROPS.trees.autumn : PROPS.bushDenseOrange[0], x, y });
    } else if (pines.has(c)) {
      const h = hashAt(x, y) % 7;
      props.push({ frame: h < 5 ? PROPS.trees.pineSingle[h % 3]! : PROPS.trees.pine, x, y });
    } else if (water.has(c) && !keep.has(c) && hashAt(x, y) % 23 === 0 && water.has(k(x, y - 1))) {
      props.push({ frame: PROPS.lilyPad[hashAt(x, y) % 2]!, x, y });
    } else if (grid[y]![x] === '.' && !keep.has(c) && hashAt(x, y) % 37 === 0) {
      props.push({ frame: PROPS.flowers[hashAt(x, y) % 3]!, x, y });
    }
  }
  props.sort((a, b) => a.y - b.y || a.x - b.x);
  const land = grid.map((r, y) => r.map((ch, x) => {
    const c = k(x, y);
    return water.has(c) ? '~' : built.has(c) ? 'x' : mountain.has(c) ? 'M' : bog.has(c) ? 'b' : autumn.has(c) ? 'a' : pines.has(c) ? 'p' : ch === 'T' ? 'T' : '.';
  }).join(''));
  const out: Overworld = { w: W, h: H, seed, rows: grid.map((r) => r.join('')), fills, props, nodes, roads, looks, land, arts, crossings };
  if (R && plan) {
    out.regions = R.map((g) => ({ ...g }));
    out.regionOf = Array.from({ length: H }, (_, y) => String.fromCharCode(...plan.at.subarray(y * W, (y + 1) * W).map((r) => 97 + r)));
  }
  return out;
}

/**
 * The way along the roads from one place to another, as one polyline in cell units, or undefined
 * when no road joins them. A* over the road cells, not the place-to-place links: the road builder
 * runs a new road along an old one where it can, so two roads often share a stretch and part
 * mid-way, and a walk must turn off at that fork rather than go on to the far place and back.
 */
export function routeBetween(ow: Pick<Overworld, 'nodes' | 'roads'>, from: string, to: string): Array<[number, number]> | undefined {
  const a = ow.nodes[from], b = ow.nodes[to];
  if (!a || !b) return undefined;
  if (from === to) return [[a.x, a.y]];
  // each road as a chain of cells; a cell two roads share is where they meet
  const links = new Map<number, Set<number>>();
  const id = (x: number, y: number) => y * 4096 + x;
  const link = (p: number, q: number) => {
    if (!links.has(p)) links.set(p, new Set());
    if (!links.has(q)) links.set(q, new Set());
    links.get(p)!.add(q); links.get(q)!.add(p);
  };
  for (const r of ow.roads) {
    if (!ow.nodes[r.a] || !ow.nodes[r.b]) continue;
    for (let i = 0; i < r.path.length - 1; i++) {
      const [ax, ay] = r.path[i]!, [bx, by] = r.path[i + 1]!;
      const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay));
      let last = id(Math.floor(ax), Math.floor(ay));
      for (let s = 1; s <= n; s++) {
        const c = id(Math.floor(ax + ((bx - ax) * s) / n), Math.floor(ay + ((by - ay) * s) / n));
        link(last, c); last = c;
      }
    }
  }
  const start = id(Math.floor(a.x), Math.floor(a.y)), goal = id(Math.floor(b.x), Math.floor(b.y));
  if (!links.has(start) || !links.has(goal)) return undefined;
  const gx = goal % 4096, gy = Math.floor(goal / 4096);
  const g = new Map<number, number>([[start, 0]]), came = new Map<number, number>();
  const heap = new Heap();
  heap.push(0, start);
  while (heap.size) {
    const cur = heap.pop();
    if (cur === goal) break;
    for (const next of links.get(cur)!) {
      const ng = g.get(cur)! + 1;
      if (ng < (g.get(next) ?? Infinity)) {
        g.set(next, ng); came.set(next, cur);
        heap.push(ng + Math.abs((next % 4096) - gx) + Math.abs(Math.floor(next / 4096) - gy), next);
      }
    }
  }
  if (!g.has(goal)) return undefined;
  const cells: Array<[number, number]> = [];
  for (let c: number | undefined = goal; c !== undefined; c = came.get(c)) cells.push([(c % 4096) + 0.5, Math.floor(c / 4096) + 0.5]);
  cells.reverse();
  // keep only the ends and the elbows
  return cells.filter((p, i) => i === 0 || i === cells.length - 1 || !((cells[i - 1]![0] === p[0] && p[0] === cells[i + 1]![0]) || (cells[i - 1]![1] === p[1] && p[1] === cells[i + 1]![1])));
}

/** A point `dist` cells along a polyline, and which way it is heading there. */
export function pointAlong(path: ReadonlyArray<readonly [number, number]>, dist: number): { x: number; y: number; dx: number; dy: number; done: boolean } {
  let left = Math.max(0, dist);
  for (let i = 0; i < path.length - 1; i++) {
    const [ax, ay] = path[i]!, [bx, by] = path[i + 1]!;
    const l = Math.hypot(bx - ax, by - ay);
    if (left <= l) { const t = l ? left / l : 0; return { x: ax + (bx - ax) * t, y: ay + (by - ay) * t, dx: Math.sign(bx - ax), dy: Math.sign(by - ay), done: false }; }
    left -= l;
  }
  const last = path[path.length - 1]!, before = path[path.length - 2] ?? last;
  return { x: last[0], y: last[1], dx: Math.sign(last[0] - before[0]), dy: Math.sign(last[1] - before[1]), done: true };
}

/** Props that lie flat on the ground or the water (decks, stepping stones, flagstones, lily pads, flowers): they cast no shadow. */
export const FLAT_FRAMES: ReadonlySet<number> = new Set<number>([
  ...PLANKS.ew, ...PLANKS.ns, LIQUID.water!.base + LIQUID_STONE[0]!, LIQUID.water!.base + LIQUID_STONE[1]!, 59, ...PROPS.lilyPad, ...PROPS.flowers,
]);

/** Walking pace on the maps, cells a second (a Mario-style stroll, not a teleport). */
export const WALK_CELLS_PER_S = 5;

export const pathLength = (path: ReadonlyArray<readonly [number, number]>): number => path.reduce((s, q, i) => (i ? s + Math.hypot(q[0] - path[i - 1]![0], q[1] - path[i - 1]![1]) : 0), 0);

/**
 * The chart's own props and mud in the season's country (docs/24 K6). The generator places an
 * autumn-gold ring round the bog, pine copses and a mud flat whatever the kit; this swaps them
 * for what grows there now: snowy firs, bare trees and drifted snow in winter; cacti, red rock and
 * packed sand in the desert; fruit trees and round bushes in the jungle; teal fungus, dark rock and
 * crystal in the Underdark; dead trees, black crags and glowing vents in the Ashlands; green oaks round the bog
 * in a temperate spring or summer; pines in the north. Pure, keyed by cell, so a season always
 * dresses the same valley the same way. `kit` comes from `overworldKit`; `season` is undefined
 * when the World has no clock (the chart then stays as drawn). Keeps, cottages and flagstones
 * stay as built; in the desert the grey peaks turn to red rock.
 */
export function dressForKit(ow: Pick<Overworld, 'fills' | 'props' | 'seed'>, kit: string, season: string | undefined): { fills: MapProp[]; props: MapProp[] } {
  const T = PROPS.trees;
  const golden = new Set<number>([...T.autumnSingle, T.autumn, ...PROPS.bushDenseOrange]);
  const pine = new Set<number>([...T.pineSingle, T.pine]);
  const bogRock = new Set<number>(PROPS.rocksBrown), bogBush = new Set<number>(PROPS.bushSmall);
  const mud = new Set<number>([MUD, FILLS.brownTex]);
  const peaks: Record<number, number> = { 618: 621, 620: 623, 675: 678, 676: 679, 677: 680 };
  const pick = <F,>(list: readonly F[], x: number, y: number) => list[((Math.imul(x + 3, 2654435761) ^ Math.imul(y + 7, 40503) ^ ow.seed) >>> 0) % list.length]!;
  type Swap = { golden?: readonly number[]; pine?: readonly number[]; rock?: readonly number[]; bush?: readonly number[]; mud?: readonly number[]; peaks?: boolean };
  const swaps: Record<string, Swap | undefined> = {
    winterwood: { golden: [T.dead, T.snowPineSingle[0], T.snowPineSingle[2], T.dead, T.snowPineSingle2[1]], pine: [...T.snowPineSingle, ...T.snowPineSingle2, T.snowPine], rock: [PROPS.rocks, PROPS.boulder], bush: [T.stump, PROPS.rocks, T.dead], mud: [FILLS.snow] },
    dunes: { golden: [...PROPS.cacti, T.dead, PROPS.rocksOrange[0], PROPS.rocksOrange[2]], pine: [...PROPS.cacti, ...PROPS.roundBush], rock: [PROPS.rocks, PROPS.rocks, PROPS.rocks, PROPS.bones[1]], bush: [PROPS.rocks, T.dead], mud: [GROUND.peach2!.base], peaks: true },
    jungle: { golden: [T.fruit, T.oak, ...PROPS.roundBush, ...PROPS.bushDense], pine: [T.oak, T.fruit, ...PROPS.bushDense] },
    highland: { golden: [PROPS.boulder, ...T.pineSingle, PROPS.rocks], bush: [PROPS.rocks, PROPS.boulder] },
    // docs/23 Y9: teal growth for the glowing fungus, dark rock pillars and crystal for the woods
    underdark: { golden: [...PROPS.starPlant, PROPS.rocksDarkGrey[0], PROPS.rocksDarkGrey[2]], pine: [...PROPS.starPlant, PROPS.crystals.grey[0], PROPS.rocksDarkGrey[4]], rock: [PROPS.rocks, PROPS.crystals.grey[1]], bush: [...PROPS.shrubs.teal], mud: [FILLS.greyRock] },
    // docs/23 Y10: burnt trees and black crags for the woods, red rock for the peaks, cinders for the mud
    ashlands: { golden: [T.dead, T.dead, PROPS.rocksDarkGrey[0], PROPS.rocksDarkGrey[2]], pine: [T.dead, PROPS.rocksDarkGrey[4], PROPS.rocksRed[0], PROPS.crystals.orange], rock: [PROPS.rocks, PROPS.rocksDarkGrey[1]], bush: [T.stump, PROPS.rocks, PROPS.roundBushBrown[0]], mud: [FILLS.greyRock], peaks: true },
    pinewood: season === 'autumn' ? undefined : { golden: [...T.pineSingle, T.pine, T.dead] },
    forest: season === 'spring' || season === 'summer' ? { golden: [...T.oakSingle, T.oak, ...PROPS.bushDense] } : undefined,
  };
  const s = swaps[kit];
  if (!s) return { fills: ow.fills, props: ow.props };
  const props = ow.props.map((p) => {
    if (s.peaks && peaks[p.frame] !== undefined) return { ...p, frame: peaks[p.frame]! };
    const to = golden.has(p.frame) ? s.golden : pine.has(p.frame) ? s.pine : bogRock.has(p.frame) ? s.rock : bogBush.has(p.frame) ? s.bush : undefined;
    return to ? { ...p, frame: pick(to, p.x, p.y) } : p;
  });
  const fills = s.mud ? ow.fills.map((f) => (mud.has(f.frame) ? { ...f, frame: pick(s.mud!, f.x, f.y) } : f)) : ow.fills;
  return { fills, props };
}
